// firebaseHelpers.ts

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  QueryConstraint,
  DocumentData,
  WithFieldValue,
  PartialWithFieldValue,
  QuerySnapshot,
  where,
  setDoc,
  limit,
  orderBy,
  Query,
  Timestamp,
  writeBatch,
  increment,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from '../auth/firebaseConfig';
import { Listing } from '../components/property/property-card/PropertyCardTypes';
import { SearchParams } from '../pages/browse-listings/BrowseListingsPage';
import { normalizeText } from '../data/locations';
import { deleteImage } from './firebaseStorageHelpers';
import { User } from '../types/userTypes';

interface ShareTracking {
  propertyId: string;
  sharedWith: string;
  sharedAt: Timestamp | Date;
}
export interface AutoApplyUser {
  userId: string;
  isEnabled: boolean;
  activatedAt?: Timestamp;
  expiresAt?: Timestamp;
  shareCount: number;
  sharedProperties: ShareTracking[];
  preferences?: AutoApplyPreferences; // Added preferences
  createdAt: Timestamp;
  lastUpdated: Timestamp;
  stats: {
    totalApplications: number;
    successfulApplications: number;
  };
}

interface AutoApplyPreferences {
  maxPrice: number;
  minPrice: number;
  locations: string[];
  propertyTypes: string[];
  minBedrooms: number;
  minBathrooms: number;
  mustHaveParking: boolean;
  petFriendly: boolean;
  availableFrom: Date | null;
  keywords: string[];
}

export interface AutoApplyStatus {
  isActive: boolean;
  isFeatureEnabledByUser: boolean;
  shareCount: number;
  sharesNeeded: number;
  expiryDate?: Date;
  recentApplications: AutoApplyApplication[];
  preferences?: AutoApplyPreferences;
  stats: {
    totalApplications: number;
    successfulApplications: number;
  };
}

export interface AutoApplyApplication {
  propertyId: string;
  appliedAt: Timestamp | Date;
  status: 'pending' | 'successful' | 'failed';
  propertyDetails?: {
    title: string;
    location: string;
    price: number;
    type: string;
  };
}

interface AutoApplyPreferences {
  maxPrice: number;
  minPrice: number;
  locations: string[];
  propertyTypes: string[];
  minBedrooms: number;
  minBathrooms: number;
  mustHaveParking: boolean;
  petFriendly: boolean;
  availableFrom: Date | null;
  keywords: string[];
}

// Generic type for Firestore documents
export type FirestoreDocument<T = DocumentData> = T & { id: string };

export const insertDocument = async <T extends DocumentData>(
  collectionName: string,
  data: WithFieldValue<T>,
  documentId?: string
): Promise<string> => {
  try {
    if (documentId) {
      // If documentId is provided, use setDoc to create/overwrite the document
      const docRef = doc(db, collectionName, documentId);
      await setDoc(docRef, data);
      return documentId;
    } else {
      // If no documentId is provided, use addDoc to let Firebase generate an ID
      const collectionRef = collection(db, collectionName);
      const docRef = await addDoc(collectionRef, data);
      return docRef.id;
    }
  } catch (error) {
    throw error;
  }
};

// Get a single document by ID
export const getDocumentById = async <T extends DocumentData>(
  collectionName: string,
  docId: string
): Promise<FirestoreDocument<T> | null> => {
  try {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as T;
      // Spread data last to preserve document fields, including custom 'id' if present
      return {
        ...data,
        firestoreDocId: docSnap.id,
        id: (data as any).id || docSnap.id,
      } as FirestoreDocument<T>;
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
};

export const getUserById = async (userId: string): Promise<User | null> => {
  try {
    // Guard against undefined, null, or empty string userIds
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
      return null;
    }

    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as User;
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
};

export const getDocumentByField = async <T extends DocumentData>(
  collectionName: string,
  fieldName: string,
  fieldValue: string | number
): Promise<T | null> => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, where(fieldName, '==', fieldValue));
    const querySnapshot: QuerySnapshot<T> = (await getDocs(q)) as QuerySnapshot<T>;

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    const data = doc.data();
    // Preserve all document data fields (spread last to keep data intact)
    return { ...data } as T;
  } catch (error) {
    throw error;
  }
};

// Get all documents from a collection
export const getAllDocuments = async <T extends DocumentData>(
  collectionName: string
): Promise<FirestoreDocument<T>[]> => {
  try {
    const collectionRef = collection(db, collectionName);
    const querySnapshot = await getDocs(collectionRef);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as T),
    }));
  } catch (error) {
    throw error;
  }
};

// Update a document
export const updateDocument = async <T extends DocumentData>(
  collectionName: string,
  docId: string,
  data: PartialWithFieldValue<T>
): Promise<void> => {
  try {
    const docRef = doc(db, collectionName, docId);
    await updateDoc(docRef, data as PartialWithFieldValue<DocumentData>);
  } catch (error) {
    throw error;
  }
};

// Delete a document
export const deleteDocument = async (collectionName: string, docId: string): Promise<void> => {
  try {
    const docRef = doc(db, collectionName, docId);
    await deleteDoc(docRef);
  } catch (error) {
    throw error;
  }
};

// Update site metrics (increment/decrement counters)
export const updateSiteMetrics = async (metrics: Record<string, number>): Promise<void> => {
  try {
    const metricsRef = doc(db, 'siteMetrics', 'global');
    const updates: Record<string, any> = {};

    // Convert numeric values to increment operations
    Object.entries(metrics).forEach(([key, value]) => {
      updates[key] = increment(value);
    });

    // Add lastUpdated timestamp
    updates.lastUpdated = serverTimestamp();

    await updateDoc(metricsRef, updates);
  } catch (error) {
    console.error('Error updating site metrics:', error);
    // Don't throw error to prevent signup/listing creation from failing
    // if metrics update fails
  }
};

// Query documents
export const queryDocuments = async <T extends DocumentData>(
  collectionName: string,
  constraints: QueryConstraint[]
): Promise<FirestoreDocument<T>[]> => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, ...constraints);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => {
      const data = doc.data() as T;
      // Preserve document data, ensure 'id' exists (use custom id if present, else Firestore doc ID)
      return {
        ...data,
        id: (data as any).id || doc.id,
      } as FirestoreDocument<T>;
    });
  } catch (error) {
    throw error;
  }
};

export const fetchListings = async (
  page: number,
  pageSize: number,
  searchParams: SearchParams,
  sortOption: string
): Promise<{ listings: Listing[]; total: number }> => {
  try {
    let q: Query<DocumentData> = collection(db, 'listings');

    // BEST PRACTICE: Apply equality filters first (most efficient)
    // Status is always 'active' - this is highly selective
    q = query(q, where('status', '==', 'active'));

    // Apply other equality filters
    if (searchParams.propertyType !== 'Any') {
      q = query(q, where('type', '==', searchParams.propertyType));
    }

    if (searchParams.berRating !== 'Any') {
      q = query(q, where('berRating', '==', searchParams.berRating));
    }

    if (searchParams.listingIntent !== 'Any') {
      q = query(q, where('listingIntent', '==', searchParams.listingIntent));
    }

    // BEST PRACTICE: Apply range/inequality filters in order of selectivity
    // Location is often the most selective filter (narrows down results significantly)
    const hasLocationFilter = searchParams.location && searchParams.location.trim() !== '';
    if (hasLocationFilter) {
      const userInput = searchParams.location.trim().toLowerCase();
      const searchTerm = normalizeText(userInput);

      q = query(
        q,
        where('location', '>=', searchTerm),
        where('location', '<=', searchTerm + '\uf8ff')
      );
      // Add orderBy for the range field to optimize index usage
      q = query(q, orderBy('location', 'asc'));
    }

    // Price filters - often selective
    const hasPriceFilter =
      searchParams.minPrice > 0 || (searchParams.maxPrice != null && searchParams.maxPrice > 0);
    if (hasPriceFilter && !hasLocationFilter) {
      // Only apply if location filter isn't already using a range
      // Firestore allows range on one field, then we need orderBy
      if (searchParams.minPrice > 0) {
        q = query(q, where('price', '>=', searchParams.minPrice));
      }
      if (searchParams.maxPrice != null && searchParams.maxPrice > 0) {
        q = query(q, where('price', '<=', searchParams.maxPrice));
      }
      q = query(q, orderBy('price', 'asc'));
    }

    // Bedrooms filter - moderately selective
    const hasBedroomsFilter = searchParams.bedrooms > 0;
    if (hasBedroomsFilter && !hasLocationFilter && !hasPriceFilter) {
      q = query(q, where('bedrooms', '>=', searchParams.bedrooms));
      q = query(q, orderBy('bedrooms', 'asc'));
    }

    // Apply sorting based on user preference
    // Note: If we already have an orderBy from filters, we add secondary sort
    if (sortOption === 'priceLowToHigh') {
      if (!hasPriceFilter || hasLocationFilter) {
        q = query(q, orderBy('price', 'asc'));
      }
    } else if (sortOption === 'priceHighToLow') {
      if (!hasPriceFilter || hasLocationFilter) {
        q = query(q, orderBy('price', 'desc'));
      }
    } else {
      // Default sort by dateAdded
      q = query(q, orderBy('dateAdded', 'desc'));
    }

    // Get total count first
    const totalSnapshot = await getDocs(q);
    const totalDocs = totalSnapshot.docs;

    // IMPORTANT: Apply client-side filtering for constraints we couldn't apply in query
    // This happens when we have multiple range filters (Firestore limitation)
    let filteredDocs = totalDocs;

    // Apply price filter client-side if needed
    if (hasPriceFilter && hasLocationFilter) {
      filteredDocs = filteredDocs.filter((doc) => {
        const data = doc.data();
        const price = data.price || 0;
        const meetsMin = searchParams.minPrice > 0 ? price >= searchParams.minPrice : true;
        const meetsMax =
          searchParams.maxPrice != null && searchParams.maxPrice > 0
            ? price <= searchParams.maxPrice
            : true;
        return meetsMin && meetsMax;
      });
    }

    // Apply bedrooms filter client-side if needed
    if (hasBedroomsFilter && (hasLocationFilter || hasPriceFilter)) {
      filteredDocs = filteredDocs.filter((doc) => {
        const data = doc.data();
        return (data.bedrooms || 0) >= searchParams.bedrooms;
      });
    }

    // Apply bathrooms filter client-side (always, since it's less selective)
    if (searchParams.bathrooms > 0) {
      filteredDocs = filteredDocs.filter((doc) => {
        const data = doc.data();
        return (data.bathrooms || 0) >= searchParams.bathrooms;
      });
    }

    // Apply availableFrom filter client-side (temporal filters are often less selective)
    if (searchParams.availableFrom instanceof Date) {
      const availableFromTimestamp = Timestamp.fromDate(searchParams.availableFrom);
      filteredDocs = filteredDocs.filter((doc) => {
        const data = doc.data();
        const docTimestamp = data.availableFrom;
        if (!docTimestamp) return false;
        return docTimestamp >= availableFromTimestamp;
      });
    }

    const total = filteredDocs.length;

    // Apply pagination to filtered results
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedDocs = filteredDocs.slice(startIndex, endIndex);

    // Convert to Listing objects
    const listings = paginatedDocs.map(
      (doc) =>
        ({
          id: Number(doc.id),
          ...doc.data(),
        }) as Listing
    );

    return { listings, total };
  } catch (error: any) {
    throw error;
  }
};

export const updateUserInListings = async (
  userId: string,
  updatedUserData: Partial<User>
): Promise<void> => {
  try {
    const listingsRef = collection(db, 'listings');
    const q = query(listingsRef, where('propertyOwnerUid', '==', userId));
    const querySnapshot = await getDocs(q);

    const batch = writeBatch(db);

    querySnapshot.forEach((doc) => {
      const listingRef = doc.ref;
      batch.update(listingRef, { user: updatedUserData });
    });

    await batch.commit();
  } catch (error) {
    throw error;
  }
};

export const deactivateAccount = async (userId: string): Promise<void> => {
  try {
    await updateDocument('users', userId, {
      isDeactivated: true,
      deactivatedAt: Timestamp.now(),
      status: 'inactive',
    });

    // Update associated listings to be inactive
    const listings = await queryDocuments('listings', [where('propertyOwnerUid', '==', userId)]);

    const batch = writeBatch(db);
    listings.forEach((listing) => {
      const listingRef = doc(db, 'listings', listing.id.toString());
      batch.update(listingRef, { status: 'inactive' });
    });

    await batch.commit();
  } catch (error) {
    throw error;
  }
};

export const deleteAccount = async (userId: string): Promise<void> => {
  try {
    // Get user data first
    const userData = await getUserById(userId);
    if (!userData) {
      throw new Error('User not found');
    }

    // Delete profile photo if exists
    if (userData.photoUrl) {
      await deleteImage(userData.photoUrl);
    }

    // Delete all user's listings and their images
    const listings = await queryDocuments('listings', [where('propertyOwnerUid', '==', userId)]);

    for (const listing of listings) {
      // Delete listing images
      if (listing.images && listing.images.length > 0) {
        for (const imageUrl of listing.images) {
          await deleteImage(imageUrl);
        }
      }
      // Delete the listing document
      await deleteDocument('listings', listing.id.toString());
    }

    // Delete all conversations
    const conversations = await queryDocuments('conversations', [
      where('participants', 'array-contains', userId),
    ]);

    for (const conversation of conversations) {
      await deleteDocument('conversations', conversation.id);
    }

    // Finally, delete user document
    await deleteDocument('users', userId);
  } catch (error) {
    throw error;
  }
};

// Helper function to check Auto Apply eligibility
export const checkAutoApplyEligibility = async (
  userId: string
): Promise<{
  isEligible: boolean;
  sharesNeeded: number;
  expiryDate?: Date;
}> => {
  try {
    const autoApplyUser = await getDocumentById<AutoApplyUser>('autoApplyUsers', userId);

    if (!autoApplyUser) {
      return { isEligible: false, sharesNeeded: 2 };
    }

    const isActive =
      autoApplyUser.isEnabled &&
      autoApplyUser.expiresAt &&
      autoApplyUser.expiresAt.toDate() > new Date();

    if (isActive) {
      return {
        isEligible: true,
        sharesNeeded: 0,
        expiryDate: autoApplyUser.expiresAt?.toDate(),
      };
    }

    // If expired, need 4 shares to reactivate
    const baseSharesNeeded = autoApplyUser.expiresAt ? 4 : 2;
    const sharesNeeded = Math.max(0, baseSharesNeeded - autoApplyUser.shareCount);

    return {
      isEligible: false,
      sharesNeeded,
      expiryDate: autoApplyUser.expiresAt?.toDate(),
    };
  } catch (error) {
    return { isEligible: false, sharesNeeded: 2 };
  }
};

export const fetchAutoApplyData = async (
  autoApplyData: FirestoreDocument<AutoApplyUser> | null
): Promise<AutoApplyStatus> => {
  try {
    // Get user's auto apply document
    if (autoApplyData === null) {
      // Return default status for new users
      return {
        isActive: false,
        isFeatureEnabledByUser: false,
        shareCount: 0,
        sharesNeeded: 2,
        stats: {
          totalApplications: 0,
          successfulApplications: 0,
        },
        recentApplications: [],
      };
    }

    // Check if feature is currently active - explicitly handle undefined cases
    const now = new Date();
    const isActive: boolean = Boolean(
      autoApplyData.isEnabled && autoApplyData.expiresAt && autoApplyData.expiresAt.toDate() > now
    );

    // Calculate shares needed based on status
    let sharesNeeded = 0;
    if (!isActive) {
      // If expired, need 4 shares to reactivate, otherwise need 2 to activate
      const baseRequired = autoApplyData.expiresAt ? 4 : 2;
      sharesNeeded = Math.max(0, baseRequired - (autoApplyData.shareCount || 0));
    }

    // Fetch recent applications
    const applicationsRef = collection(db, 'autoApplyApplications');
    const applicationsQuery = query(
      applicationsRef,
      where('userId', '==', autoApplyData.userId),
      orderBy('appliedAt', 'desc'),
      limit(10)
    );
    const applicationsSnapshot = await getDocs(applicationsQuery);
    const recentApplications = applicationsSnapshot.docs.map((doc) => ({
      ...(doc.data() as AutoApplyApplication),
    }));

    // Calculate stats with default values
    const stats = {
      totalApplications: autoApplyData.stats?.totalApplications ?? 0,
      successfulApplications: autoApplyData.stats?.successfulApplications ?? 0,
    };

    // Construct return object with all required fields
    const status: AutoApplyStatus = {
      isActive,
      isFeatureEnabledByUser: autoApplyData.isEnabled,
      shareCount: autoApplyData.shareCount ?? 0,
      sharesNeeded,
      expiryDate: autoApplyData.expiresAt?.toDate(),
      recentApplications,
      preferences: autoApplyData.preferences,
      stats,
    };
    return status;
  } catch (error) {
    throw new Error('Failed to fetch Auto Apply data');
  }
};

// Helper function to check if Auto Apply is ready for activation
export const checkAutoApplyActivation = async (
  userId: string
): Promise<{
  canActivate: boolean;
  currentShares: number;
  requiredShares: number;
  isReactivation: boolean;
}> => {
  try {
    const autoApplyRef = doc(db, 'autoApplyUsers', userId);
    const autoApplyDoc = await getDoc(autoApplyRef);

    if (!autoApplyDoc.exists()) {
      return {
        canActivate: false,
        currentShares: 0,
        requiredShares: 2,
        isReactivation: false,
      };
    }

    const data = autoApplyDoc.data() as AutoApplyUser;
    const isReactivation = !!data.expiresAt;
    const requiredShares = isReactivation ? 4 : 2;
    const currentShares = data.shareCount ?? 0;

    return {
      canActivate: currentShares >= requiredShares,
      currentShares,
      requiredShares,
      isReactivation,
    };
  } catch (error) {
    throw new Error('Failed to check Auto Apply activation status');
  }
};

// Helper function to get share status
export const getShareStatus = async (
  userId: string,
  propertyId: string
): Promise<{
  hasShared: boolean;
  shareTime?: Date;
  platform?: string;
}> => {
  try {
    const autoApplyRef = doc(db, 'autoApplyUsers', userId);
    const autoApplyDoc = await getDoc(autoApplyRef);

    if (!autoApplyDoc.exists()) {
      return { hasShared: false };
    }

    const data = autoApplyDoc.data() as AutoApplyUser;
    const share = data.sharedProperties?.find((s) => s.propertyId === propertyId);

    if (!share) {
      return { hasShared: false };
    }

    return {
      hasShared: true,
      shareTime: share.sharedAt instanceof Timestamp ? share.sharedAt.toDate() : share.sharedAt, // Check type before calling toDate()
      platform: share.sharedWith,
    };
  } catch (error) {
    throw new Error('Failed to get share status');
  }
};
